import {gql} from '@apollo/client';
import {Heading, Pane, TickCircleIcon} from 'evergreen-ui';
import React, {useEffect, useReducer} from 'react';
import {
    Column,
    EmailLabelWithCopy,
    EnrichedTable,
    ErrorDisplay,
    LinkToNewTabKeepContext,
    LoadingSpinner,
    PORTAL_NAVY,
    RelayConnection,
    SimpleDropdown,
    SortBy,
} from '../../../common';
import {useApplicationRootUrl} from 'common/component/ApplicationRootContext';
import {formatISODateStr} from '../../../common/util/date';
import {useLazyQuery$} from '../../../graphqlsupport';
import {SortOrder} from '../../../placement/domains';
import {useObservable} from '../../../rxsupport';
import {
    RelayPagination,
    RelayPaginationActionPayload,
    RelayPaginationState,
    reduceRelayPaginationAction,
    useRelayPagination,
} from '../../pagination';
import {CLIENT_PROFILE_PROGRESS_PORTRAITS_PATH} from '../domain';
import {splitSourceIdentifier} from './splitSourceIdentifier';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = ['10', '25', '50', '100'];
const STATUS_GREEN = '#52BD94';
const EMPTY_CELL = '-';

const SOURCE_PAGE_LABELS: Record<string, string> = {
    PROGRESS_PORTRAIT: 'Progress Portrait',
};

const DOWNLOAD_RESULT_LABELS: Record<string, string> = {
    SUCCESS: 'Successful',
};

// FE column attr -> BE whitelist sort key (ProgressPortraitFileDownloadRequestQueryAdapter#resolveSort).
const SORT_KEY_BY_COLUMN: Partial<Record<keyof DownloadAuditRow, string>> = {
    requestedAt: 'requestedAt',
    requestedByFirstName: 'requestedBy',
};
const DEFAULT_SORT: SortBy<DownloadAuditRow> = {column: 'requestedAt', order: SortOrder.DESC};

type DownloadAuditRow = {
    guid: string;
    requestedAt: string;
    requestedByFirstName: string | null;
    requestedByLastName: string | null;
    requestedByEmail: string | null;
    clientGuid: string | null;
    clientProfile: { fullName: string | null; medicalRecordNumber: string | null } | null;
    sourceIdentifier: string | null;
    sourcePage: string;
    downloadResult: string;
    /** Derived from sourceIdentifier on render, not queried; each is its own column. */
    portraitId?: string;
    reportDate?: string;
}

type DownloadAuditResponse = RelayConnection<DownloadAuditRow> & { totalElements: number };

type DownloadAuditState = {pagination: RelayPaginationState; pageSize: number; sortBy: SortBy<DownloadAuditRow>};
type DownloadAuditAction =
    | RelayPaginationActionPayload
    | {type: 'SET_PAGE_SIZE'; pageSize: number}
    | {type: 'SET_SORT_BY'; sortBy: SortBy<DownloadAuditRow>};

export function downloadAuditReducer(state: DownloadAuditState, action: DownloadAuditAction): DownloadAuditState {
    switch (action.type) {
        case 'RELAY_PAGINATION_ACTION':
            return {...state, pagination: reduceRelayPaginationAction(state.pagination, action)};
        case 'SET_PAGE_SIZE':
            return {...state, pageSize: action.pageSize, pagination: new RelayPaginationState('0', action.pageSize)};
        case 'SET_SORT_BY':
            return {...state, sortBy: action.sortBy, pagination: new RelayPaginationState('0', state.pageSize)};
        default:
            return state;
    }
}

const ResultCell = ({result}: {result: string}) => {
    const label = DOWNLOAD_RESULT_LABELS[result] ?? result;
    if (result === 'SUCCESS') {
        return (
            <span className="inline-flex items-center" style={{color: STATUS_GREEN}}>
                <TickCircleIcon size={14} color={STATUS_GREEN} marginRight={6}/>
                {label}
            </span>
        );
    }
    return <span>{label}</span>;
};

export const PROGRESS_PORTRAIT_DOWNLOAD_AUDIT_QUERY = gql`
    query progressPortraitFileDownloadRequests(
        $first: Int = 10,
        $after: String,
        $sortBy: String,
        $sortDir: String
    ) {
        progressPortraitFileDownloadRequests(
            first: $first,
            after: $after,
            sortBy: $sortBy,
            sortDir: $sortDir
        ) {
            edges {
                cursor
                node {
                    guid
                    requestedAt
                    requestedByFirstName
                    requestedByLastName
                    requestedByEmail
                    clientGuid
                    clientProfile {
                        fullName
                        medicalRecordNumber
                    }
                    sourceIdentifier
                    sourcePage
                    downloadResult
                }
            }
            totalElements
            pageInfo {
                hasPreviousPage
                hasNextPage
                startCursor
                endCursor
            }
        }
    }
`;

export const ProgressPortraitDownloadAuditTable = () => {
    const rootUrl = useApplicationRootUrl();
    const [{pagination, pageSize, sortBy}, dispatch] = useReducer(downloadAuditReducer, {
        pagination: new RelayPaginationState('0', DEFAULT_PAGE_SIZE),
        pageSize: DEFAULT_PAGE_SIZE,
        sortBy: DEFAULT_SORT,
    });

    const sortVars = () => ({
        sortBy: SORT_KEY_BY_COLUMN[sortBy.column as keyof DownloadAuditRow] ?? 'requestedAt',
        sortDir: sortBy.order,
    });

    const {execute, data, isRunning$, error} = useLazyQuery$<
        {first: number; after: string; sortBy: string; sortDir: string},
        {progressPortraitFileDownloadRequests: DownloadAuditResponse}
    >(PROGRESS_PORTRAIT_DOWNLOAD_AUDIT_QUERY, {...pagination.toGraphQlParams(), ...sortVars()});

    useEffect(() => {
        execute({...pagination.toGraphQlParams(), ...sortVars()});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination, sortBy]);

    const loading = useObservable(isRunning$, true);
    const totalRows = data?.progressPortraitFileDownloadRequests?.totalElements ?? 0;
    const {onPaginationClick} = useRelayPagination(dispatch, totalRows);
    const rows = data?.progressPortraitFileDownloadRequests?.edges
        .map((edge) => ({...edge.node, ...splitSourceIdentifier(edge.node.sourceIdentifier)})) ?? [];

    if (error) {
        return <ErrorDisplay error={error} title="Error loading progress portrait download log"/>;
    }

    return (
        <Pane className="flex flex-col" flexGrow={1}>
            <Pane display="flex" justifyContent="flex-end" alignItems="center" marginBottom={16}>
                <SimpleDropdown options={PAGE_SIZE_OPTIONS} selected={pageSize.toString()}
                                minWidth={'2rem'}
                                textColor={'gray-700'}
                                setSelected={(newPageSize) => dispatch({type: 'SET_PAGE_SIZE', pageSize: parseInt(newPageSize)})}/>
                <RelayPagination pageInfo={data?.progressPortraitFileDownloadRequests.pageInfo}
                                 count={totalRows}
                                 onClick={onPaginationClick}/>
            </Pane>
            <Pane borderTop="1px solid #E6E8EC" marginBottom={20}/>
            <Heading size={600} color={PORTAL_NAVY} marginTop={8} marginBottom={20}>Download History</Heading>
            <EnrichedTable<DownloadAuditRow>
                firstColumn={'requestedAt'}
                sortBy={sortBy}
                setSortBy={(col: Column<DownloadAuditRow>) => {
                    if (!SORT_KEY_BY_COLUMN[col.attr as keyof DownloadAuditRow]) {
                        return;
                    }
                    const order = col.attr === sortBy.column && sortBy.order === SortOrder.DESC
                        ? SortOrder.ASC
                        : SortOrder.DESC;
                    dispatch({type: 'SET_SORT_BY', sortBy: {column: col.attr, order}});
                }}
                styleOverride={{
                    trHeader: 'portal-history-header text-left select-none text-sm',
                }}
                columns={[
                    {attr: 'requestedAt', name: 'Date/Time', span: 2},
                    {attr: 'requestedByFirstName', name: 'User', span: 2},
                    {attr: 'clientProfile', name: 'Client Name', span: 2},
                    {attr: 'portraitId', name: 'Portrait ID', span: 2},
                    {attr: 'reportDate', name: 'Report Date', span: 2},
                    {attr: 'sourcePage', name: 'Page', span: 2},
                    {attr: 'downloadResult', name: 'Result', span: 2},
                ]}
                rowsData={rows}
                keyColumn={'guid'}
                cellDisplayFunc={(row, column) => {
                    if (column.attr === 'requestedAt') {
                        return formatISODateStr(row.requestedAt, 'MM/dd/yyyy hh:mm a');
                    }
                    if (column.attr === 'requestedByFirstName') {
                        const name = `${row.requestedByFirstName ?? ''} ${row.requestedByLastName ?? ''}`.trim();
                        if (name === '') {
                            return EMPTY_CELL;
                        }
                        return row.requestedByEmail
                            ? <EmailLabelWithCopy email={row.requestedByEmail} label={name}/>
                            : name;
                    }
                    if (column.attr === 'clientProfile') {
                        const mrn = row.clientProfile?.medicalRecordNumber;
                        const fullName = row.clientProfile?.fullName?.trim();
                        const label = fullName && mrn
                            ? `${fullName} (${mrn})`
                            : fullName || mrn || EMPTY_CELL;
                        if (row.clientGuid && (fullName || mrn)) {
                            return (
                                <LinkToNewTabKeepContext
                                    link={`${rootUrl}/clients/${row.clientGuid}/${CLIENT_PROFILE_PROGRESS_PORTRAITS_PATH}`}>
                                    <span className="underline text-blue-portal-link">
                                        {label}
                                    </span>
                                </LinkToNewTabKeepContext>
                            );
                        }
                        return label;
                    }
                    if (column.attr === 'portraitId') {
                        return row.portraitId ?? EMPTY_CELL;
                    }
                    if (column.attr === 'reportDate') {
                        return row.reportDate ?? EMPTY_CELL;
                    }
                    if (column.attr === 'sourcePage') {
                        return SOURCE_PAGE_LABELS[row.sourcePage] ?? row.sourcePage;
                    }
                    if (column.attr === 'downloadResult') {
                        return <ResultCell result={row.downloadResult}/>;
                    }
                    return '';
                }}
                emptyMessage={
                    <div className="w-full flex items-center">
                        <div className="m-auto pt-8">No progress portrait downloads yet</div>
                    </div>
                }
                loadingFeat={{
                    loading,
                    loadingDisplayElement: <LoadingSpinner spacing={20} height={'30vh'}/>,
                }}
            />
        </Pane>
    );
};
